import { useEffect, useMemo, useRef, useState } from 'react'
import { Pause, Play, Timer, X } from 'lucide-react'
import { useTaskStore } from '../../store/taskStore'
import { enterFullscreen, exitFullscreen } from '../../services/fullscreen'

const formatTime = (seconds: number) => `${String(Math.floor(seconds / 3600)).padStart(2, '0')}:${String(Math.floor(seconds / 60) % 60).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`

export default function ZenMode({ iconOnly = false }: { iconOnly?: boolean }) {
  const [open, setOpen] = useState(false)
  const [running, setRunning] = useState(false)
  const [elapsed, setElapsed] = useState(0)
  const [visible, setVisible] = useState(document.visibilityState === 'visible')
  const startedAt = useRef<string | null>(null)
  const enteredFullscreen = useRef(false)
  const tasks = useTaskStore((s) => s.tasks)
  const addFocusSession = useTaskStore((s) => s.addFocusSession)
  const task = useMemo(() => tasks.find((item) => item.isToday && item.status !== 'done') ?? tasks.find((item) => item.status === 'in-progress') ?? tasks.find((item) => item.status === 'todo'), [tasks])

  useEffect(() => {
    const updateVisibility = () => setVisible(document.visibilityState === 'visible')
    document.addEventListener('visibilitychange', updateVisibility)
    return () => document.removeEventListener('visibilitychange', updateVisibility)
  }, [])

  useEffect(() => {
    if (!open) return
    document.documentElement.dataset.zenMode = 'true'
    if (!document.fullscreenElement) enterFullscreen().then((entered) => { enteredFullscreen.current = entered })
    return () => { delete document.documentElement.dataset.zenMode }
  }, [open])

  useEffect(() => {
    if (!running || !visible) return
    const timer = window.setInterval(() => setElapsed((seconds) => seconds + 1), 1000)
    return () => window.clearInterval(timer)
  }, [running, visible])

  const close = () => {
    setRunning(false)
    if (startedAt.current && elapsed > 0) addFocusSession({ startedAt: startedAt.current, endedAt: new Date().toISOString(), focusedSeconds: elapsed, taskId: task?.id })
    startedAt.current = null
    setElapsed(0)
    setOpen(false)
    if (enteredFullscreen.current) { enteredFullscreen.current = false; void exitFullscreen() }
  }

  const start = () => {
    if (!startedAt.current) startedAt.current = new Date().toISOString()
    setRunning(true)
  }

  const triggerButton = iconOnly ? (
    <button
      type="button"
      onClick={() => setOpen(true)}
      className="flex h-11 w-11 items-center justify-center rounded-full bg-white/90 dark:bg-slate-900/90 backdrop-blur-md border border-slate-200/80 dark:border-slate-700/80 text-violet-600 dark:text-violet-400 shadow-lg shadow-violet-500/10 hover:shadow-xl hover:shadow-violet-500/20 hover:bg-violet-50 dark:hover:bg-slate-800 transition-all duration-200 hover:scale-110 active:scale-95 focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:outline-none"
      title="Modo Zen"
      aria-label="Modo Zen"
    >
      <Timer size={18} />
    </button>
  ) : (
    <button
      type="button"
      className="flex-1 flex items-center justify-center gap-1.5 bg-violet-50 dark:bg-violet-950/40 border border-violet-200 dark:border-violet-800/60 text-violet-700 dark:text-violet-300 hover:bg-violet-100 dark:hover:bg-violet-900/60 rounded-xl py-2 text-xs font-semibold transition-colors"
      onClick={() => setOpen(true)}
    >
      <Timer size={13} /> <span>Modo Zen</span>
    </button>
  )

  return <>
    {triggerButton}
    {open && <div className="fixed inset-0 z-[19001] flex flex-col bg-slate-950 text-white" role="dialog" aria-modal="true" aria-labelledby="zen-title">
      <header className="flex items-center justify-between border-b border-slate-800 p-4"><span className="text-sm text-slate-300">Zeen mode · notificações do TaskPriority silenciadas</span><button className="rounded-lg p-2 hover:bg-slate-800" onClick={close} aria-label="Sair do Zeen mode"><X /></button></header>
      <main className="mx-auto flex w-full max-w-2xl flex-1 flex-col items-center justify-center p-6 text-center"><p className="text-sm font-medium text-violet-300">Foco sem interrupções</p><h1 id="zen-title" className="mt-3 text-3xl font-semibold sm:text-5xl">{task?.title ?? 'Defina sua próxima tarefa'}</h1><p className="mt-4 max-w-xl text-slate-400">O cronômetro conta apenas enquanto esta aba estiver ativa. Para silenciar notificações de outros apps, ative o Assistente de foco do Windows.</p><div className="mt-10 font-mono text-6xl font-bold tabular-nums sm:text-8xl">{formatTime(elapsed)}</div>{!visible && <p className="mt-4 rounded-full bg-amber-500/15 px-3 py-1 text-sm text-amber-300">Cronômetro pausado: aba fora de foco</p>}<button onClick={() => running ? setRunning(false) : start()} className="mt-10 flex items-center gap-2 rounded-full bg-violet-600 px-6 py-3 font-medium hover:bg-violet-500">{running ? <Pause size={20} /> : <Play size={20} />}{running ? 'Pausar' : 'Iniciar'}</button></main>
    </div>}
  </>
}
